import { Grid } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { CustomTabPanel } from 'views/Production/CustomPanel/index';
import ProductiveHours from '../components/ProductiveHours';
import DateRangeDiv from 'views/Utilization/HeaderMenu/DateRangeDiv';
import GridAnalytics from '../components/GridAnalytics';
import HoursBreakdown from '../components/HoursBreakdown';
import FuelConsumption from '../components/FuelConsumption';
import Suggestions from 'views/Utilization/components/Suggestions/Suggestions';
import AssetView from '../components/Asset/AssetWrapper';
import { aggregateFuelConsumption } from '../AllFleet';
import { getShiftHours } from '../utils';
import { useAppSelector } from 'store/hook';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  value: number;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  assetOverview: any;
  aggregation: string;
  setAggregation: any;
  shiftNumber: number;
  setShiftNumber: any;
  shiftWiseAnalysis: boolean;
  setShiftWiseAnalysis: any;
  utilizationType: string;
  setUtilizationType: any;
  setDetailsVal: any;
  setAnalyticsData: any;
  setHoursBreakDown: any;
  setUserSelections: any;
  setSuggestionData: any;
  setFuelConsumption: any;
  selectedChartSource: any;
  setSelectedChartSource: any;
  handleFetchCustomDateRangeData: ({
    aggregation,
  }: {
    aggregation: string;
  }) => void;
  handleFetchCustomOnAggregationChange: ({
    aggregation,
    startDate,
    endDate,
  }: {
    aggregation: string;
    startDate: Date;
    endDate: Date;
  }) => void;
}

const Hauler = ({
  value,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  assetOverview,
  aggregation,
  setAggregation,
  shiftWiseAnalysis,
  setShiftWiseAnalysis,
  shiftNumber,
  setShiftNumber,
  utilizationType,
  setUtilizationType,
  setDetailsVal,
  setAnalyticsData,
  setHoursBreakDown,
  setUserSelections,
  setSuggestionData,
  setFuelConsumption,
  selectedChartSource,
  setSelectedChartSource,
  handleFetchCustomDateRangeData,
  handleFetchCustomOnAggregationChange,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { customerCode } = useCustomerInfo();

  const loadingStates = useAppSelector(
    (state) => state.utilizationReducer.loadingStates
  );

  const data = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet
  );

  const dataSummary = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.summary
  );

  const dataAssets = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.assets
  );

  const summaryData = useMemo(() => {
    return {
      utilization:
        dataSummary.hauler_avg_utilization_engine_by_shift_hours * 100,
      utilizationDiff:
        dataSummary.hauler_avg_utilization_engine_by_shift_hours_diff,
      idling: dataSummary.hauler_avg_idling_duration,
      idlingDiff: dataSummary.hauler_avg_idling_duration_diff,
      shiftHours: getShiftHours(customerCode) * 60, // 10.5 in mins,
      shiftHoursDiff: 0,
      engineHours: dataSummary.hauler_avg_utilization_engine_working_minute,
      engineHoursDiff:
        dataSummary.hauler_avg_utilization_engine_working_minute_diff,
      engineActiveMinutes:
        dataSummary.hauler_avg_utilization_engine_active_minute,
      engineActiveMinutesDiff:
        dataSummary.hauler_avg_utilization_engine_active_minute_diff,
    };
  }, [dataSummary, customerCode]);

  const haulerBreakDown = useMemo(() => {
    return {
      productiveHours: dataSummary.hauler_total_engine_working_minutes,
      productiveHoursDiff: dataSummary.hauler_total_engine_working_minutes_diff,
      idling: dataSummary.hauler_total_idling_minutes,
      idlingDiff: dataSummary.hauler_total_idling_minutes_diff,
      noOfAssets: dataSummary.number_of_haulers,
      dateRange: dataSummary.date_range,
      engineHours: dataSummary.hauler_total_engine_active_minutes,
      shiftHours: dataSummary.hauler_total_shift_hours,
      engineHoursDiff: dataSummary.hauler_total_engine_active_minutes_diff,
      shiftHoursDiff: dataSummary.hauler_total_shift_hours_diff,
    };
  }, [dataSummary]);

  const fuelData = useMemo(() => {
    return {
      total:
        Number(dataSummary.hauler_total_productive_fuel_consumption_l) +
        Number(dataSummary.hauler_total_idle_fuel_consumption_l),
      totalDiff:
        (Number(dataSummary.hauler_total_productive_fuel_consumption_l_diff) *
          Number(dataSummary.hauler_total_idle_fuel_consumption_l_diff)) /
        100,
      productive: dataSummary.hauler_total_productive_fuel_consumption_l,
      productiveDiff:
        dataSummary.hauler_total_productive_fuel_consumption_l_diff,
      idle: dataSummary.hauler_total_idle_fuel_consumption_l,
      idleDiff: dataSummary.hauler_total_idle_fuel_consumption_l_diff,
    };
  }, [dataSummary]);

  const dataSummaryChart = useAppSelector(
    (state) => state.utilizationReducer.data.allFleet.summaryChart.haulers
  );

  const haulerAssets = useMemo(() => {
    return dataAssets.filter((item) =>
      ['haul_truck', 'light_truck', 'heavy_truck'].includes(item.asset_type)
    );
  }, [dataAssets]);
  const aggregatedData = useMemo(() => {
    return aggregateFuelConsumption(haulerAssets);
  }, [haulerAssets]);

  return (
    <>
      <CustomTabPanel value={value} index={1}>
        <div>
          <ProductiveHours
            chartData={data.fleetPeriodSummary.haulers}
            detailsData={data.fleetDetails.haulers}
            assetType="hauler"
            setDetailsVal={setDetailsVal}
            setUserSelections={setUserSelections}
            selectedChartSource={selectedChartSource}
            setSelectedChartSource={setSelectedChartSource}
          />

          <DateRangeDiv
            isSelected={isSelected}
            setIsSelected={setIsSelected}
            handleDateChange={handleDateChange}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            yesterday={yesterday}
            ignoreYtd={value === 2}
            aggregation={aggregation}
            setAggregation={setAggregation}
            shiftWiseAnalysis={shiftWiseAnalysis}
            setShiftWiseAnalysis={setShiftWiseAnalysis}
            shiftNumber={shiftNumber}
            setShiftNumber={setShiftNumber}
            utilizationType={utilizationType}
            setUtilizationType={setUtilizationType}
            handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
            handleFetchCustomOnAggregationChange={
              handleFetchCustomOnAggregationChange
            }
          />

          <GridAnalytics
            assetOverview={assetOverview}
            isLoading={loadingStates.isLoadingFleetSummary}
            isSelected={isSelected}
            summary={summaryData}
            score={data.score}
            data={dataSummaryChart}
            assetType="Haul Truck"
            aggregation={aggregation}
            shiftNumber={shiftNumber}
            shiftWiseAnalysis={shiftWiseAnalysis}
            utilizationType={utilizationType}
            setAnalyticsData={setAnalyticsData}
          />

          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            sx={{
              paddingTop: '15px',
              color: palette.textPrimary,
            }}
          >
            <Grid item lg={9} sm={12} md={12}>
              <Grid container spacing={2} justifyContent="space-between">
                <HoursBreakdown
                  enableDarkTheme={enableDarkTheme}
                  title={t('hauler_hours_breakdown')}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  customerCode={customerCode}
                  data={haulerBreakDown}
                  setHoursBreakDown={setHoursBreakDown}
                />
                <FuelConsumption
                  title={t('hauler_fuel_consumption')}
                  isLoading={loadingStates.isLoadingFleetSummary}
                  data={fuelData}
                  setFuelConsumption={setFuelConsumption}
                />
              </Grid>
            </Grid>
            <Suggestions
              minHeight="400px"
              data={aggregatedData}
              assetType="hauler"
              setSuggestionData={setSuggestionData}
            />
          </Grid>
          <AssetView
            isSelected={isSelected}
            data={haulerAssets}
            utilizationType={utilizationType}
          />
        </div>
      </CustomTabPanel>
    </>
  );
};

export default memo(Hauler);
